import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  IconButton,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";
import { Formik, Form, Field, FormikProps, FieldArray } from "formik";
import * as Yup from "yup";
import {
  commonDateFieldProps,
  commonTableContainerProps,
  commonTextFieldProps,
  smallButtonProp,
  tableCellStyles,
  tableHeaderStyles,
} from "../../../utils/styles";
import { Clear, Delete } from "@mui/icons-material";
import { inventoryApi } from "./apiService";
import { Loading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { useSearchQuery } from "../../../hooks/useSearchQuery";

// Validation schema
const validationSchema = Yup.object().shape({
  partNumber: Yup.string()
    .min(2, "Part number must be at least 2 characters")
    .required("Part number is required"),
  manufacturer: Yup.string().optional(),
  supplierName: Yup.string().optional(),
  amount: Yup.string().required("Price is required"),
});

interface InventoryFormData {
  categoryId: string;
  partNumber: string;
  manufacturer: string;
  supplierName: string;
  amount: string;
  supplierId: string | null;
  warrantyStartDate: string | null;
  warrantyPeriod: string;
  warrantyEndDate: string | null;
  warrantyNumber: string;
}

interface InventoryActionModalProps {
  open: boolean;
  onClose: () => void;
  category: string;
  categoryId: string;
  viewMode?: boolean;
  item?: any | null;
  isEdit?: boolean;
  fetchInventoryItems: () => void;
}

const EditableCell = React.memo(
  ({
    name,
    label,
    type = "text",
    value,
    setFieldValue,
    compactTextFieldProps,
    index,
    viewMode = false,
    values,
  }: {
    name: string;
    label: string;
    type: string;
    value: any;
    setFieldValue: (name: string, value: any) => void;
    compactTextFieldProps: any;
    index: number;
    viewMode?: boolean;
    values?: any;
  }) => {
    const onChange = (e: any) => {
      if (viewMode) return;
      const fieldPath = `inventoryItems.${index}.${name}`;
      if (type === "number") {
        setFieldValue(fieldPath, Number(e.target.value));
      } else {
        setFieldValue(fieldPath, e.target.value);
        const startDateRaw = values[index]?.warrantyStartDate;
        if (name === "warrantyPeriod" && startDateRaw) {
          const monthsToAdd = Number(e.target.value);
          const startDate = new Date(startDateRaw);

          // Create a new Date object to avoid mutating the original
          const endDate = new Date(
            startDate.getFullYear(),
            startDate.getMonth() + monthsToAdd,
            startDate.getDate()
          );
          setFieldValue(
            `inventoryItems.${index}.warrantyEndDate`,
            endDate?.toISOString().split("T")[0]
          );
        }
      }
    };

    return (
      <TableCell {...tableCellStyles}>
        <TextField
          {...compactTextFieldProps}
          name={name}
          // label={label}
          type={type}
          value={value}
          onChange={onChange}
          disabled={viewMode}
          sx={{
            ...compactTextFieldProps.sx,
            "& .MuiInput-root": {
              ...compactTextFieldProps.sx["& .MuiInput-root"],
              backgroundColor: viewMode ? "rgba(0, 0, 0, 0.04)" : "transparent",
            },
          }}
        />
      </TableCell>
    );
  }
);

const GarageAutoComplete = React.memo(
  ({
    garages,
    index,
    values,
    setFieldValue,
    compactTextFieldProps,
    loading = false,
  }: {
    garages: any[];
    index: number;
    values: InventoryFormData[];
    setFieldValue: (name: string, value: any) => void;
    compactTextFieldProps: any;
    loading: boolean;
  }) => {
    return (
      <TableCell {...tableCellStyles}>
        <Autocomplete
          freeSolo
          options={garages}
          clearIcon={<Clear sx={{ fontSize: "0.85rem" }} />}
          value={values[index].supplierName || ""}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.name || ""
          }
          loading={loading}
          onChange={(_, newValue) => {
            setFieldValue(
              `inventoryItems.${index}.supplierName`,
              newValue?.name || ""
            );
            setFieldValue(
              `inventoryItems.${index}.supplierId`,
              newValue?.id || null
            );
          }}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === "input") {
              setFieldValue(
                `inventoryItems.${index}.supplierName`,
                newInputValue
              );
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              {...compactTextFieldProps}
              placeholder="Search for supplier name"
              InputProps={{
                ...params.InputProps,
                ...compactTextFieldProps?.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                "& .MuiAutocomplete-input": {
                  padding: "1px 0 !important",
                  fontSize: "0.55rem",
                },
                minWidth: "150px",
                width: "100%",
                ...compactTextFieldProps?.sx,
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest} style={{ padding: "0px 1px" }}>
                <Box sx={{ p: 0, px: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "0.55rem", mb: 0.5, p: 0 }}
                  >
                    {option.name}
                  </Typography>
                </Box>
              </li>
            );
          }}
          noOptionsText={
            loading
              ? "Searching..."
              : `No matching Garage found. You can still type to add manually.`
          }
        />
      </TableCell>
    );
  }
);

const InventoryRow = React.memo(
  ({
    index,
    values,
    setFieldValue,
    remove,
    compactTextFieldProps,
    viewMode = false,
    garages,
    garagesLoading,
  }: {
    index: number;
    values: InventoryFormData[];
    setFieldValue: (name: string, value: any) => void;
    remove: (index: number) => void;
    compactTextFieldProps: any;
    viewMode?: boolean;
    garages: any[];
    garagesLoading: boolean;
  }) => {
    return (
      <TableRow>
        <EditableCell
          name="partNumber"
          label="Part Number"
          type="text"
          value={values[index].partNumber}
          setFieldValue={setFieldValue}
          compactTextFieldProps={compactTextFieldProps}
          index={index}
          viewMode={viewMode}
          values={values}
        />
        <EditableCell
          name="amount"
          label="Price"
          type="text"
          value={values[index].amount}
          setFieldValue={setFieldValue}
          compactTextFieldProps={compactTextFieldProps}
          index={index}
          viewMode={viewMode}
          values={values}
        />
        <EditableCell
          name="manufacturer"
          label="Manufacturer"
          type="text"
          value={values[index].manufacturer}
          setFieldValue={setFieldValue}
          compactTextFieldProps={compactTextFieldProps}
          index={index}
          viewMode={viewMode}
          values={values}
        />
        <GarageAutoComplete
          garages={garages}
          index={index}
          values={values}
          setFieldValue={setFieldValue}
          compactTextFieldProps={compactTextFieldProps}
          loading={garagesLoading}
        />
        <EditableCell
          name="warrantyStartDate"
          label="Warranty Start Date"
          type="date"
          value={values[index].warrantyStartDate}
          setFieldValue={setFieldValue}
          compactTextFieldProps={compactTextFieldProps}
          index={index}
          viewMode={viewMode}
          values={values}
        />
        <EditableCell
          name="warrantyPeriod"
          label="Warranty Period"
          type="text"
          value={values[index].warrantyPeriod}
          setFieldValue={setFieldValue}
          compactTextFieldProps={compactTextFieldProps}
          index={index}
          viewMode={viewMode}
          values={values}
        />
        <EditableCell
          name="warrantyEndDate"
          label="Warranty End Date"
          type="date"
          value={values[index].warrantyEndDate}
          setFieldValue={setFieldValue}
          compactTextFieldProps={compactTextFieldProps}
          index={index}
          viewMode={viewMode}
          values={values}
        />
        <EditableCell
          name="warrantyNumber"
          label="Warranty Number"
          type="text"
          value={values[index].warrantyNumber}
          setFieldValue={setFieldValue}
          compactTextFieldProps={compactTextFieldProps}
          index={index}
          viewMode={viewMode}
          values={values}
        />
        <TableCell>
          <IconButton onClick={() => remove(index)}>
            <Delete
              sx={{ cursor: "pointer", fontSize: "0.65rem" }}
              color="error"
            />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  }
);

const InventoryActionModal = ({
  open,
  onClose,
  category,
  viewMode = false,
  item,
  isEdit = false,
  categoryId,
  fetchInventoryItems,
}: InventoryActionModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const { data: garages, isLoading: garagesLoading } = useSearchQuery(
    "GARAGE",
    ""
  );

  const compactTextFieldProps = React.useMemo(
    () => ({
      variant: "standard" as const,
      size: "small" as const,
      fullWidth: true,
      InputProps: {
        style: { padding: "1px" },
      },
      sx: {
        "& .MuiInput-root": {
          fontSize: "0.55rem",
        },
        "& .MuiInput-input": {
          padding: "1px 0",
        },
        "& .MuiInput-underline:before": {
          borderBottomWidth: "0.1px",
          borderColor: "grey.300",
        },
        "& .MuiInput-underline:after": {
          borderBottomWidth: "0.1px",
          borderColor: "grey.300",
        },
        "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
          borderBottomWidth: "0.1px",
          borderColor: "grey.300",
        },
      },
    }),
    []
  );

  const getInitialValues = (): InventoryFormData[] => {
    if (item) {
      return [
        {
          categoryId: item.categoryId || "",
          partNumber: item.partNumber || "",
          manufacturer: item.manufacturer || "",
          supplierName: item.supplierName || "",
          supplierId: item.supplierId || null,
          amount: item.amount || "",
          warrantyStartDate: item.warrantyStartDate || null,
          warrantyPeriod: item.warrantyPeriod || "",
          warrantyEndDate: item.warrantyEndDate || null,
          warrantyNumber: item.warrantyNumber || "",
        },
      ];
    }
    return [
      {
        categoryId,
        partNumber: "",
        manufacturer: "",
        supplierName: "",
        supplierId: null,
        amount: "",
        warrantyStartDate: null,
        warrantyPeriod: "",
        warrantyEndDate: null,
        warrantyNumber: "",
      },
    ];
  };

  const handleSubmit = async (values: {
    inventoryItems: InventoryFormData[];
  }) => {
    try {
      setIsSubmitting(true);
      await inventoryApi.createInventoryItems(values.inventoryItems);
      onClose();
      fetchInventoryItems();
      showToast("Inventory items saved successfully!", "success");
    } catch (error) {
      console.error("Error saving inventory items:", error);
      showToast("Failed to save inventory items. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return <Loading message="Saving..." fullScreen={true} />;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1, fontSize: "1rem" }}>
        {isEdit ? `Edit ${category}` : `Add New ${category}`}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", height: "70vh", p: 0 }}
      >
        <Formik
          initialValues={{ inventoryItems: getInitialValues() }}
          // validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            values,
          }) => (
            <Form
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {/* Table Container - Fixed height with scrollable body */}
              <Box sx={{ flex: 1, overflow: "hidden", px: 2 }}>
                <TableContainer
                  sx={{
                    ...commonTableContainerProps,
                    height: "100%",
                    overflowY: "auto",
                    overflowX: "auto",
                    position: "relative",
                    border: "0.5px solid #e0e0e0",
                  }}
                >
                  <Table size="small" aria-label="inventory table" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          size="small"
                          sx={{ ...tableHeaderStyles, fontSize: "0.55rem" }}
                        >
                          {category} Number
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={{ ...tableHeaderStyles, fontSize: "0.55rem" }}
                        >
                          Price
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={{ ...tableHeaderStyles, fontSize: "0.55rem" }}
                        >
                          Manufacturer
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={{ ...tableHeaderStyles, fontSize: "0.55rem" }}
                        >
                          Supplier Name
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={{ ...tableHeaderStyles, fontSize: "0.55rem" }}
                        >
                          Warranty Start Date
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={{ ...tableHeaderStyles, fontSize: "0.55rem" }}
                        >
                          Warranty Period
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={{ ...tableHeaderStyles, fontSize: "0.55rem" }}
                        >
                          Warranty End Date
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={{ ...tableHeaderStyles, fontSize: "0.55rem" }}
                        >
                          Warranty Number
                        </TableCell>
                        {!viewMode && (
                          <TableCell
                            size="small"
                            sx={{ ...tableHeaderStyles, fontSize: "0.55rem" }}
                          >
                            Actions
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <FieldArray name="inventoryItems">
                        {({ push, remove }) => (
                          <>
                            {values.inventoryItems.map((item, index) => (
                              <InventoryRow
                                key={index}
                                index={index}
                                values={values.inventoryItems}
                                setFieldValue={setFieldValue}
                                remove={remove}
                                compactTextFieldProps={compactTextFieldProps}
                                viewMode={viewMode}
                                garages={garages!}
                                garagesLoading={garagesLoading}
                              />
                            ))}
                            <Box
                              sx={{
                                px: 1,
                                mb: 1,
                              }}
                            >
                              <Button
                                {...smallButtonProp}
                                variant="text"
                                onClick={() => {
                                  setFieldValue("inventoryItems", [
                                    ...values.inventoryItems,
                                    {
                                      categoryId,
                                      partNumber: "",
                                      amount: "",
                                      manufacturer: "",
                                      supplierName: "",
                                      warrantyStartDate: "",
                                      warrantyPeriod: "",
                                      warrantyEndDate: "",
                                      warrantyNumber: "",
                                    },
                                  ]);
                                }}
                              >
                                Add {category}
                              </Button>
                            </Box>
                          </>
                        )}
                      </FieldArray>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              {/* Dialog Actions - Fixed at bottom */}
              <DialogActions
                sx={{ p: 2, borderTop: "1px solid #e0e0e0", mt: "auto" }}
              >
                <Button
                  onClick={onClose}
                  {...smallButtonProp}
                  disabled={isSubmitting}
                >
                  {viewMode ? "Close" : "Cancel"}
                </Button>
                {!viewMode && (
                  <Button
                    type="submit"
                    {...smallButtonProp}
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : isEdit ? "Update" : "Save"}
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default InventoryActionModal;
